<template>
  <section class="members-point-section">
    <div class="members-point-section__header">
      <p class="members-point-section__current-point">
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0 7C0 10.866 3.13401 14 7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7ZM10.8567 6.1955C10.8567 4.8185 9.7767 4.0625 8.1567 4.0625H3.8457V10.3625H5.2857V8.3285H8.1567C9.7767 8.3285 10.8567 7.5905 10.8567 6.1955ZM9.3897 6.2045C9.3897 6.9245 8.8497 7.1405 8.0397 7.1405H5.2857V5.2685H8.0397C8.8497 5.2685 9.3897 5.4845 9.3897 6.2045Z"
            fill="white"
          />
        </svg>
        <span v-text="currentPoint.toLocaleString()" />
      </p>

      <!-- TODO: GOLDより上のランクについてはリリースしないため、伏せておく -->
      <p v-if="nextRank && nextRank !== 'platinum'" class="members-point-section__next-rank">
        <span class="members-point-section__next-rank__text" v-html="rankBenefitsLink" />
        <span
          class="members-point-section__next-rank__point"
          v-text="`${nextRankPoint.toLocaleString()}P`"
        />
      </p>
    </div>

    <point-gauge-parts
      class="members-point-section__gauge"
      :membersRankData="membersRankData"
      :isFreePlan="isFreePlan"
    />

    <div class="members-point-section__expiration">
      <membership-point-expiration-parts
        v-if="membersRankData.expirationPoint && membersRankData.expirationDate"
        :point="membersRankData.expirationPoint"
        :expirationDate="membersRankData.expirationDate"
        :backLink="backLink"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import {
  UserDataType,
  MembersRankDataType,
} from '@/components/MypageTopPage/MembershipCardPane.vue'
import PointGaugeParts from '@/components/MypageTopPage/MembershipCardPane/parts/PointGaugeParts.vue'
import Const from '@/util/Const'
import I18n from '@/locales/I18n'
import MembershipPointExpirationParts from '@/components/MypageTopPage/MembershipCardPane/parts/MembershipPointExpirationParts.vue'
import { PointExpirationBackLinkType } from '@/views/MypagePage/MypagePointExpirationDatePage.vue'

/**
 * マイページ: 会員証 表示モードセクション
 */
export default defineComponent({
  name: 'MembersPointSection',
  components: {
    MembershipPointExpirationParts,
    PointGaugeParts,
  },
  props: {
    userData: {
      type: Object as PropType<UserDataType>,
      default: () => ({}),
      require: true,
    },
    membersRankData: {
      type: Object as PropType<MembersRankDataType>,
      default: () => ({}),
      require: true,
    },
    /**
     * 戻るボタンで戻る画面を指定
     */
    backLink: {
      type: String as PropType<PointExpirationBackLinkType>,
      default: 'mypage',
    },
  },
  setup(props) {
    /**
     * 無料ユーザー判定取得
     */
    const isFreePlan = computed(() => props.userData.isFreePlan)

    /**
     * 会員種別取得
     */
    const membershipType = computed(() => (props.userData.isFreePlan ? 'free' : 'paid'))

    /**
     * 現在のポイント
     */
    const currentPoint = computed(() => props.membersRankData.point)

    /**
     * 次のランクのインデックス番号を取得
     */
    const nextRankIndex = computed(
      () =>
        Const.MEMBERS_RANK_NAME_POINTS.findIndex(
          (rankPoint) => rankPoint.rank === props.membersRankData.currentRank,
        ) + 1,
    )

    /**
     * 次のランクまでのポイントを取得
     */
    const nextRankPoint = computed(() => {
      const nextPoint = Const.MEMBERS_RANK_NAME_POINTS[nextRankIndex.value]
        ? Const.MEMBERS_RANK_NAME_POINTS[nextRankIndex.value].point
        : undefined
      if (nextPoint) {
        return nextPoint - currentPoint.value
      }
      return 0
    })

    /**
     * 次のランクを取得
     */
    const nextRank = computed(() =>
      Const.MEMBERS_RANK_NAME_POINTS[nextRankIndex.value]
        ? Const.MEMBERS_RANK_NAME_POINTS[nextRankIndex.value].rank
        : undefined,
    )
    /**
     * 会員ランク特典説明へのリンク
     */
    const rankBenefitsLink = computed(() =>
      I18n.t(
        `MypagePage.MypageTopPage.membersRank.nextRank.${membershipType.value}.${nextRank.value}`,
        {
          url:
            I18n.locale === 'ja'
              ? Const.EXTERNAL_LINKS.ABOUT_MEMBER_RANK.JA
              : Const.EXTERNAL_LINKS.ABOUT_MEMBER_RANK.EN,
        },
      ).toString(),
    )

    return {
      membershipType,
      isFreePlan,
      currentPoint,
      nextRankPoint,
      nextRank,
      rankBenefitsLink,
    }
  },
})
</script>

<style lang="scss" scoped>
.members-point-section {
  display: flex;
  width: 100%;
  max-width: 800px;
  padding: 8px 14px 7px;
  margin-left: auto;
  margin-right: auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(14.5px);
  border-radius: 10px;
  color: $white50;

  &__header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  &__current-point {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 3px;
    color: $white;
    font-kerning: none;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.385;
    letter-spacing: 0.02em;
  }

  &__next-rank {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 3px;
    width: 100%;
    color: $white;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: 0.02em;

    &__text {
      color: $white50;
    }

    &__point {
      font-size: 11px;
      font-weight: 600;
      line-height: 1.09;
      letter-spacing: 0.02em;
    }
  }

  &__gauge {
    margin-top: 9px;
  }

  &__expiration {
    margin-top: 17px;
  }
}
</style>

<style lang="scss">
.members-point-section {
  &__next-rank {
    &__text {
      a {
        color: $white70;
        text-decoration: underline;
      }
    }
  }
}
</style>
